import axios, { AxiosResponse } from 'axios';
import {
  ProcessedTransactionListResponse,
  ProcessedTransactionDetailsResponse,
  DeleteProcessedTransactionResponse,
  ClearProcessedTransactionsResponse,
  ProcessedTransactionRequest,
  ProcessedTransactionResponse,
  TimedPullListResponse,
  TimedPullDetails,
  TimedPullRequest,
  TimedPullResponse,
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const processedTransactionApi = {
  // Create a new processed transaction
  createProcessedTransaction: async (
    payerId: string,
    request: ProcessedTransactionRequest,
    billerId: string,
    authToken: string
  ): Promise<ProcessedTransactionResponse> => {
    const authHeader = `Basic ${btoa(`${billerId}:${authToken}`)}`;
    const response: AxiosResponse<ProcessedTransactionResponse> = await apiClient.put(
      `/protectpay/Payers/${payerId}/PaymentMethods/ProcessedTransactions/`,
      request,
      {
        headers: {
          Authorization: authHeader,
        },
      }
    );
    return response.data;
  },

  // Get all processed transactions (Admin endpoint)
  getAllProcessedTransactions: async (
    limit?: number,
    offset?: number,
    payerId?: string,
    paymentMethodId?: string,
    status?: 'COMPLETED' | 'FAILED' | 'PENDING' | 'AUTHORIZED' | 'CAPTURED' | 'VOIDED',
    startDate?: string,
    endDate?: string,
    minAmount?: number, // Kept for API compatibility but not used in UI
    maxAmount?: number, // Kept for API compatibility but not used in UI
    isReversed?: boolean,
    format?: 'json' | 'csv'
  ): Promise<ProcessedTransactionListResponse | string> => {
    const params = new URLSearchParams();

    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (payerId) params.append('payerId', payerId);
    if (paymentMethodId) params.append('paymentMethodId', paymentMethodId);
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    // minAmount and maxAmount parameters removed from request
    if (isReversed !== undefined) params.append('isReversed', isReversed.toString());
    if (format) params.append('format', format);

    const queryString = params.toString();
    const url = `/admin/protectpay/processed-transactions${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);

    // If format is CSV, return the raw text response
    if (format === 'csv') {
      return response.data as string;
    }

    return response.data as ProcessedTransactionListResponse;
  },

  // Get specific Processed transaction details (Admin endpoint)
  getProcessedTransactionDetails: async (transactionId: string): Promise<ProcessedTransactionDetailsResponse> => {
    const response: AxiosResponse<ProcessedTransactionDetailsResponse> = await apiClient.get(
      `/admin/protectpay/processed-transactions/${transactionId}`
    );
    return response.data;
  },

  // Delete a specific Processed transaction (Admin endpoint)
  deleteProcessedTransaction: async (transactionId: string): Promise<DeleteProcessedTransactionResponse> => {
    const response: AxiosResponse<DeleteProcessedTransactionResponse> = await apiClient.delete(
      `/admin/protectpay/processed-transactions/${transactionId}`
    );
    return response.data;
  },

  // Clear all Processed transactions (Admin endpoint)
  clearAllProcessedTransactions: async (): Promise<ClearProcessedTransactionsResponse> => {
    const response: AxiosResponse<ClearProcessedTransactionsResponse> = await apiClient.delete(
      '/admin/protectpay/processed-transactions'
    );
    return response.data;
  },

  // Download Processed transactions as CSV (Admin endpoint)
  downloadProcessedTransactionsCSV: async (
    limit?: number,
    offset?: number,
    payerId?: string,
    paymentMethodId?: string,
    status?: 'COMPLETED' | 'FAILED' | 'PENDING' | 'AUTHORIZED' | 'CAPTURED' | 'VOIDED',
    startDate?: string,
    endDate?: string,
    minAmount?: number, // Kept for API compatibility but not used in UI
    maxAmount?: number, // Kept for API compatibility but not used in UI
    isReversed?: boolean
  ): Promise<string> => {
    const params = new URLSearchParams();

    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (payerId) params.append('payerId', payerId);
    if (paymentMethodId) params.append('paymentMethodId', paymentMethodId);
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    // minAmount and maxAmount parameters removed from request
    if (isReversed !== undefined) params.append('isReversed', isReversed.toString());
    params.append('format', 'csv');

    const queryString = params.toString();
    const url = `/admin/protectpay/processed-transactions${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url, {
      responseType: 'text',
    });

    return response.data;
  },

  adminListTimedPulls: async (
    limit?: number,
    offset?: number,
    sourceAccountNum?: number,
    recipientAccountNum?: number,
    status?: 'PENDING' | 'COMPLETED' | 'FAILED',
    startDate?: string,
    endDate?: string
  ): Promise<TimedPullListResponse> => {
    const params = new URLSearchParams();

    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (sourceAccountNum !== undefined) params.append('sourceAccountNum', sourceAccountNum.toString());
    if (recipientAccountNum !== undefined) params.append('recipientAccountNum', recipientAccountNum.toString());
    if (status) params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const url = `/admin/protectpay/processed-transactions/timed-pulls${queryString ? `?${queryString}` : ''}`;

    const response: AxiosResponse<TimedPullListResponse> = await apiClient.get(url);
    return response.data;
  },

  adminGetTimedPullDetails: async (timedPullId: string): Promise<TimedPullDetails> => {
    const response: AxiosResponse<TimedPullDetails> = await apiClient.get(
      `/admin/protectpay/processed-transactions/timed-pulls/${timedPullId}`
    );
    return response.data;
  },

  createTimedPull: async (
    request: TimedPullRequest,
    certStr: string,
    termId: string
  ): Promise<TimedPullResponse> => {
    const authHeader = `Basic ${btoa(`${certStr}:${termId}`)}`;
    const response: AxiosResponse<TimedPullResponse> = await apiClient.put(
      `/propayAPI/TimedPull/`,
      request,
      {
        headers: {
          Authorization: authHeader,
        },
      }
    );
    return response.data;
  },
};
